import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FaPlus, FaSearch, FaAddressBook, FaPencilAlt, FaTrash, FaSpinner, FaEnvelope,
  FaPhone, FaEye, FaEyeSlash, FaFileInvoiceDollar, FaExclamationTriangle,
} from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, EmptyState, Modal, Toast, useToast } from '../components/ui'
import { money, number } from '../lib/format'
import { STATUS } from '../lib/viz'
import * as api from '../lib/api'
import { useAuth } from '../context/AuthContext'

/**
 * The address book.
 *
 * Its whole purpose is the dropdown on the invoice editor: every field filled in
 * here is a field nobody retypes later, and a VAT number entered once and
 * checked once is a VAT number that is right on every invoice afterwards.
 *
 * Add, edit and remove all sit in the same place and work the same way as they
 * do for products — one primary button in the header, a pencil and a bin on
 * every row.
 */

const BLANK = {
  name: '',
  trading_as: '',
  contact_name: '',
  email: '',
  phone: '',
  website: '',
  vat_number: '',
  registration_number: '',
  address_line1: '',
  address_line2: '',
  city: '',
  province: '',
  postal_code: '',
  country: 'South Africa',
  account_number: '',
  payment_terms_days: 30,
  notes: '',
  is_active: true,
}

const SuppliersPage = () => {
  // Deleting a supplier needs is_manager (migration 18); adding, editing and
  // hiding stay open to all staff.
  const { canManage } = useAuth()
  const [suppliers, setSuppliers] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [usage, setUsage] = useState(null)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState(null)
  const { toast, notify, notifyError, dismiss } = useToast()

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      setSuppliers(await api.getSuppliers())
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
  }, [])

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()
    return suppliers
      .filter((s) => showInactive || s.is_active)
      .filter((s) => {
        if (!query) return true
        return [s.name, s.trading_as, s.contact_name, s.email, s.phone, s.city, s.vat_number]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      })
  }, [suppliers, search, showInactive])

  // Same judgement as the product editor makes about a reused style code: warn,
  // never block. Two entries for one company is usually a slip, but a group with
  // two trading divisions is legitimate and must stay saveable.
  const nameClash = editing?.name
    ? suppliers.filter(
        (s) => s.id !== editing.id && s.name.trim().toLowerCase() === editing.name.trim().toLowerCase(),
      )
    : []

  const save = async () => {
    setBusy(true)
    setFormError(null)
    try {
      if (!editing.name.trim()) throw new Error('A supplier needs a name.')
      const { id, created_at, updated_at, ...patch } = editing
      const saved = await api.saveSupplier(id ?? null, patch)
      notify(id ? `${saved.name} updated.` : `${saved.name} added to the supplier list.`)
      setEditing(null)
      load()
    } catch (err) {
      setFormError(err)
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  const openRemove = async (supplier) => {
    setRemoving(supplier)
    setUsage(null)
    try {
      setUsage(await api.getSupplierUsage(supplier.id))
    } catch {
      setUsage({ invoices: 0, total: 0, unknown: true })
    }
  }

  const toggleActive = async (supplier) => {
    try {
      await api.setSupplierActive(supplier.id, !supplier.is_active)
      notify(
        supplier.is_active
          ? `${supplier.name} hidden from the dropdowns.`
          : `${supplier.name} is back in the dropdowns.`,
      )
      load()
    } catch (err) {
      notifyError(err)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api.deleteSupplier(removing.id)
      notify(`${removing.name} deleted.`)
      setRemoving(null)
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  if (state.loading) return <Spinner label="Loading suppliers…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load suppliers.</strong> {state.error.message}</Banner>
  }

  const set = (key, value) => setEditing((current) => ({ ...current, [key]: value }))

  return (
    <div>
      <PageHeader title="Suppliers" subtitle={`${number(suppliers.length)} companies in the address book`}>
        <Link to="/invoices" className="btn-ghost">
          <FaFileInvoiceDollar className="text-[11px]" />
          Invoices
        </Link>
        <button onClick={() => { setEditing({ ...BLANK }); setFormError(null) }} className="btn-primary">
          <FaPlus className="text-[11px]" />
          Add a supplier
        </button>
      </PageHeader>

      <Card bodyClassName="">
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline p-3">
          <div className="relative min-w-[200px] flex-1">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted"
              aria-hidden="true"
            />
            <input
              className="input pl-8"
              placeholder="Search name, contact, town or VAT number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search suppliers"
            />
          </div>
          <label className="flex items-center gap-2 text-2xs text-ink-secondary">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-hairline text-brand-navy focus:ring-brand-navy/30"
            />
            Show hidden
          </label>
        </div>

        {visible.length === 0 ? (
          <EmptyState
            icon={FaAddressBook}
            title={suppliers.length === 0 ? 'No suppliers yet' : 'Nothing matches'}
            action={
              suppliers.length === 0 ? (
                <button onClick={() => setEditing({ ...BLANK })} className="btn-primary">
                  <FaPlus className="text-[11px]" />
                  Add the first one
                </button>
              ) : (
                <button onClick={() => setSearch('')} className="btn-ghost">Clear the search</button>
              )
            }
          >
            Everything entered here fills itself in on an invoice later — VAT number, address, e-mail
            and payment terms.
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Contact</th>
                  <th>VAT number</th>
                  <th>Town</th>
                  <th className="num">Terms</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((supplier) => (
                  <tr key={supplier.id} className={supplier.is_active ? '' : 'opacity-55'}>
                    <td>
                      <button
                        onClick={() => { setEditing({ ...supplier }); setFormError(null) }}
                        className="block max-w-[240px] truncate text-left font-medium text-ink hover:text-brand-red"
                      >
                        {supplier.name}
                      </button>
                      <div className="text-2xs text-ink-muted">
                        {supplier.trading_as || supplier.account_number ? (
                          [supplier.trading_as, supplier.account_number && `Acc. ${supplier.account_number}`]
                            .filter(Boolean)
                            .join(' · ')
                        ) : (
                          '—'
                        )}
                        {!supplier.is_active && (
                          <span className="ml-1.5 font-semibold uppercase text-status-warning">Hidden</span>
                        )}
                      </div>
                    </td>
                    <td className="text-2xs">
                      {supplier.contact_name && (
                        <div className="text-ink-secondary">{supplier.contact_name}</div>
                      )}
                      {supplier.email && (
                        <a
                          href={`mailto:${supplier.email}`}
                          className="flex items-center gap-1.5 text-ink-muted hover:text-brand-red"
                        >
                          <FaEnvelope className="text-[9px]" aria-hidden="true" />
                          <span className="max-w-[170px] truncate">{supplier.email}</span>
                        </a>
                      )}
                      {supplier.phone && (
                        <a
                          href={`tel:${supplier.phone.replace(/\s/g, '')}`}
                          className="flex items-center gap-1.5 text-ink-muted hover:text-brand-red"
                        >
                          <FaPhone className="text-[9px]" aria-hidden="true" />
                          {supplier.phone}
                        </a>
                      )}
                      {!supplier.contact_name && !supplier.email && !supplier.phone && (
                        <span className="text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="text-2xs tabular-nums text-ink-secondary">
                      {supplier.vat_number || <span className="text-ink-muted">Not registered</span>}
                    </td>
                    <td className="text-2xs text-ink-secondary">{supplier.city || '—'}</td>
                    <td className="num text-2xs text-ink-secondary">{supplier.payment_terms_days} days</td>
                    <td className="whitespace-nowrap text-right">
                      <button
                        onClick={() => { setEditing({ ...supplier }); setFormError(null) }}
                        className="btn-subtle btn-sm px-1.5"
                        title="Edit"
                        aria-label={`Edit ${supplier.name}`}
                      >
                        <FaPencilAlt className="text-[10px]" />
                      </button>
                      <button
                        onClick={() => toggleActive(supplier)}
                        className="btn-subtle btn-sm px-1.5"
                        title={supplier.is_active ? 'Hide from the dropdowns' : 'Show in the dropdowns'}
                        aria-label={supplier.is_active ? `Hide ${supplier.name}` : `Show ${supplier.name}`}
                      >
                        {supplier.is_active ? <FaEye className="text-[10px]" /> : <FaEyeSlash className="text-[10px]" />}
                      </button>
                      {canManage && (
                        <button
                          onClick={() => openRemove(supplier)}
                          className="btn-subtle btn-sm px-1.5 text-brand-red"
                          title="Delete"
                          aria-label={`Delete ${supplier.name}`}
                        >
                          <FaTrash className="text-[10px]" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---- Add / edit ---- */}
      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        wide
        title={editing?.id ? `Edit ${editing.name}` : 'Add a supplier'}
        footer={
          <>
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary">
              {busy && <FaSpinner className="animate-spin" />}
              {editing?.id ? 'Save changes' : 'Add supplier'}
            </button>
          </>
        }
      >
        {editing && (
          <div className="space-y-3.5">
            {formError && <Banner tone="error">{formError.message}</Banner>}

            <div className="grid gap-3.5 sm:grid-cols-2">
              <Field label="Company name" id="sup-name">
                <input
                  id="sup-name"
                  className="input"
                  value={editing.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. Lorenzini Shirts"
                />
                {nameClash.length > 0 && (
                  <p className="field-hint text-status-warning">
                    Already in the list. That is fine if this is a second division of the same group.
                  </p>
                )}
              </Field>
              <Field label="Trading as" id="sup-trading" hint="Printed instead of the name, when it differs.">
                <input
                  id="sup-trading"
                  className="input"
                  value={editing.trading_as ?? ''}
                  onChange={(e) => set('trading_as', e.target.value)}
                  placeholder="Optional"
                />
              </Field>
            </div>

            <div className="grid gap-3.5 sm:grid-cols-3">
              <Field label="Contact person" id="sup-contact">
                <input
                  id="sup-contact"
                  className="input"
                  value={editing.contact_name ?? ''}
                  onChange={(e) => set('contact_name', e.target.value)}
                />
              </Field>
              <Field label="E-mail" id="sup-email">
                <input
                  id="sup-email"
                  type="email"
                  className="input"
                  value={editing.email ?? ''}
                  onChange={(e) => set('email', e.target.value)}
                />
              </Field>
              <Field label="Telephone" id="sup-phone">
                <input
                  id="sup-phone"
                  className="input"
                  value={editing.phone ?? ''}
                  onChange={(e) => set('phone', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <Field
                label="VAT number"
                id="sup-vat"
                hint="Ten digits starting with 4. Leave blank if they are not registered."
              >
                <input
                  id="sup-vat"
                  className="input"
                  value={editing.vat_number ?? ''}
                  onChange={(e) => set('vat_number', e.target.value)}
                  placeholder="e.g. 4123456789"
                />
                {editing.vat_number && !/^4\d{9}$/.test(editing.vat_number.replace(/\s/g, '')) && (
                  <p className="field-hint text-status-warning">
                    <FaExclamationTriangle className="mr-1 inline text-[9px]" aria-hidden="true" />
                    That is not the usual South African format. Saved as typed — foreign suppliers
                    are numbered differently.
                  </p>
                )}
              </Field>
              <Field label="Company registration number" id="sup-reg">
                <input
                  id="sup-reg"
                  className="input"
                  value={editing.registration_number ?? ''}
                  onChange={(e) => set('registration_number', e.target.value)}
                  placeholder="e.g. 2019/123456/07"
                />
              </Field>
            </div>

            <Field label="Address" id="sup-addr1">
              <input
                id="sup-addr1"
                className="input"
                value={editing.address_line1 ?? ''}
                onChange={(e) => set('address_line1', e.target.value)}
                placeholder="Street and number"
              />
            </Field>
            <input
              className="input"
              value={editing.address_line2 ?? ''}
              onChange={(e) => set('address_line2', e.target.value)}
              placeholder="Building, suburb (optional)"
              aria-label="Second address line"
            />

            <div className="grid gap-3.5 sm:grid-cols-4">
              <Field label="Town" id="sup-city">
                <input
                  id="sup-city"
                  className="input"
                  value={editing.city ?? ''}
                  onChange={(e) => set('city', e.target.value)}
                />
              </Field>
              <Field label="Province" id="sup-prov">
                <input
                  id="sup-prov"
                  className="input"
                  value={editing.province ?? ''}
                  onChange={(e) => set('province', e.target.value)}
                />
              </Field>
              <Field label="Postal code" id="sup-post">
                <input
                  id="sup-post"
                  className="input"
                  value={editing.postal_code ?? ''}
                  onChange={(e) => set('postal_code', e.target.value)}
                />
              </Field>
              <Field label="Country" id="sup-country">
                <input
                  id="sup-country"
                  className="input"
                  value={editing.country ?? ''}
                  onChange={(e) => set('country', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid gap-3.5 sm:grid-cols-2">
              <Field label="Our account number" id="sup-acc" hint="Goes onto the document as the reference.">
                <input
                  id="sup-acc"
                  className="input"
                  value={editing.account_number ?? ''}
                  onChange={(e) => set('account_number', e.target.value)}
                />
              </Field>
              <Field
                label="Payment terms (days)"
                id="sup-terms"
                hint="Sets the due date when this supplier is chosen."
              >
                <input
                  id="sup-terms"
                  type="number"
                  min="0"
                  className="input"
                  value={editing.payment_terms_days ?? 30}
                  onChange={(e) =>
                    set('payment_terms_days', e.target.value === '' ? 0 : Number(e.target.value))
                  }
                />
              </Field>
            </div>

            <Field label="Internal notes" id="sup-notes" hint="Staff only. Never appears on a document.">
              <textarea
                id="sup-notes"
                rows={2}
                className="input"
                value={editing.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
                placeholder="e.g. Delivers on Tuesdays. Ask for Marco."
              />
            </Field>
          </div>
        )}
      </Modal>

      {/* ---- Remove ---- */}
      <Modal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name ?? ''}`}
        footer={<button onClick={() => setRemoving(null)} className="btn-ghost">Cancel</button>}
      >
        {usage === null ? (
          <p className="py-6 text-center text-2xs text-ink-muted">
            <FaSpinner className="mr-2 inline animate-spin" />
            Checking what this supplier is on…
          </p>
        ) : (
          <div className="space-y-4">
            {usage.invoices > 0 ? (
              <Banner tone="warning">
                <strong>
                  {number(usage.invoices)} document{usage.invoices === 1 ? '' : 's'} worth{' '}
                  {money(usage.total)}
                </strong>{' '}
                name this supplier. Deleting keeps those documents — they carry their own copy of the
                address and VAT number — but they stop being grouped under this supplier, so it
                vanishes from any spend reporting. Hiding it is almost certainly what you want.
              </Banner>
            ) : (
              <Banner tone="info">
                This supplier is not on any document, so deleting removes it cleanly.
              </Banner>
            )}

            <div className="rounded-lg border border-hairline p-3.5">
              <h3 className="text-[13px] font-semibold text-ink">
                Hide it {usage.invoices > 0 && <span className="text-status-good-text">— recommended</span>}
              </h3>
              <p className="mt-0.5 text-2xs text-ink-muted">
                Comes out of every dropdown immediately. Every past document stays exactly as it is,
                and you can bring it back at any time.
              </p>
              <button
                onClick={async () => {
                  await toggleActive(removing)
                  setRemoving(null)
                }}
                className="btn-primary btn-sm mt-2.5"
              >
                <FaEyeSlash className="text-[10px]" />
                Hide this supplier
              </button>
            </div>

            <div className="rounded-lg border p-3.5" style={{ borderColor: `${STATUS.critical}40` }}>
              <h3 className="text-[13px] font-semibold text-ink">Delete permanently</h3>
              <p className="mt-0.5 text-2xs text-ink-muted">
                Removes the contact details for good. This cannot be undone.
              </p>
              <button onClick={remove} disabled={busy} className="btn-danger btn-sm mt-2.5">
                {busy ? <FaSpinner className="animate-spin" /> : <FaTrash className="text-[10px]" />}
                Delete permanently
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

const Field = ({ label, id, hint, children }) => (
  <div>
    <label className="label" htmlFor={id}>{label}</label>
    {children}
    {hint && <p className="field-hint">{hint}</p>}
  </div>
)

export default SuppliersPage
